import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { DealPresenceService } from '../../../services/deal-presence.service';

const MAX_FACES = 3;

const ATTENTION_MS = 1900;

const FACE_FILLS = ['deal-presence__face--brown', 'deal-presence__face--green', 'deal-presence__face--blue'];

interface PresenceFace {
  userId: string;
  displayName: string;
  initials: string;
  fill: string;
}

@Component({
  selector: 'lib-deal-presence-chip',
  standalone: true,
  imports: [NgClass, NgbPopoverModule],
  templateUrl: './deal-presence-chip.component.html',
  styleUrl: './deal-presence-chip.component.scss'
})
export class DealPresenceChipComponent {
  #presence = inject(DealPresenceService);
  #destroyRef = inject(DestroyRef);

  others = this.#presence.others;

  lockedByOther = this.#presence.lockedByOther;
  heldByMe = this.#presence.heldByMe;
  holderName = this.#presence.holderName;

  attention = signal(false);

  #knownCount = 0;
  #attentionTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const count = this.viewers().length;
      const joined = count > this.#knownCount;
      this.#knownCount = count;

      if (joined) this.#flash();
    });

    this.#destroyRef.onDestroy(() => this.#clearAttentionTimer());
  }

  viewers = computed<PresenceFace[]>(() =>
    this.others().map(viewer => ({
      userId: viewer.userId,
      displayName: viewer.displayName,
      initials: initialsOf(viewer.displayName),
      fill: fillFor(viewer.userId)
    }))
  );

  faces = computed(() => this.viewers().slice(0, MAX_FACES));

  overflow = computed(() => Math.max(this.viewers().length - MAX_FACES, 0));

  label = computed(() => {
    const viewers = this.viewers();

    // An editor is the more useful fact when there is one, so that wins the label.
    if (this.lockedByOther()) return `${this.holderName()} is editing`;
    return viewers.length === 1 ? `${viewers[0].displayName} is viewing` : `${viewers.length} viewing now`;
  });

  ariaLabel = computed(() => {
    const viewers = this.viewers();
    return viewers.length === 1
      ? `${viewers[0].displayName} is viewing this deal`
      : `${viewers.length} people are viewing this deal`;
  });

  #flash(): void {
    this.#clearAttentionTimer();
    this.attention.set(false);

    requestAnimationFrame(() => {
      this.attention.set(true);
      this.#attentionTimer = setTimeout(() => this.attention.set(false), ATTENTION_MS);
    });
  }

  #clearAttentionTimer(): void {
    if (this.#attentionTimer === null) return;

    clearTimeout(this.#attentionTimer);
    this.#attentionTimer = null;
  }
}

function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);

  if (!words.length) return '';
  if (words.length === 1) return (words[0][0] + words[0][words[0].length - 1]).toUpperCase();

  return (words[0][0] + words[1][0]).toUpperCase();
}

function fillFor(userId: string): string {
  let sum = 0;
  for (const character of userId) sum += character.charCodeAt(0);

  return FACE_FILLS[sum % FACE_FILLS.length];
}
